import { useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectAllAttendeesLoading,
  selectAllAttendeesPage,
  selectAllAttendeesPageSize,
  selectAllAttendeesSort,
  selectAllAttendeesSortDirection,
  selectAllAttendeesTotal,
  selectAllAttendeesUsers,
} from '@/store/slices/audiences/selectors';
import {
  setAllAttendeesFields,
  setAllAttendeesPage,
  setAllAttendeesPageSize,
  setAllAttendeesSort,
  resetAllAttendees,
} from '@/store/slices/audiences';
import { AttendeeUserDefault, type RegistrationFilter, UserStatus } from '@/types/all-attendees.types';
import Table from '@layouts/layout-table/layout-table';
import TableBody from '@layouts/layout-table/layout-table-body';
import OptionalComponent from '@ui/optional-component/optional-component';
import Container from '@ui/container/container';
import { useLoadAllAttendeesList } from '../../hooks/listsv2/use-load-all-attendees-list';
import { buildAttendeeFields } from './profile-column-definitions';
import ProfileListActions from './profile-list-actions';
import ProfileListRow from './profile-list-row';
import ProfileColumnsSelect from './profile-columns-select';
import AddUserToList from './add-user-to-list';
import AllProfilesBulkActions from './all-profiles-bulk-actions';
import NoProfiles from './no-profiles';
import ProfileTableHead from './profile-table-head';
import ProfileTableFooter from './profile-table-footer';
import { selectWorkingProjectRegistrationQuestions, selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import { selectActiveChannel, selectUserChannel } from '@/store/slices/user/selectors';
import { isWebinarChannel } from '@/utils/project-types/project-type-guards';
import { DEFAULT_COLUMN_IDS_WEBINAR, DEFAULT_COLUMN_IDS_TOWNHALL } from './profile-column-definitions';
import { useColumnState } from '../../hooks/listsv2/use-column-state';
import { useProfileSelection } from '../../hooks/listsv2/use-profile-selection';
import { useVisibleColumns } from '../../hooks/listsv2/use-visible-columns';

const LOCKED_STATUSES: UserStatus[] = [UserStatus.REGISTERED, UserStatus.ATTENDED, UserStatus.NO_SHOWS];
const REGISTRATION_FILTERS: RegistrationFilter[] = ['registered', 'invited', 'added'];
const ALL_PROFILES = 'all';

const AllProfiles = () => {
  const params = useParams() as { listType?: UserStatus | 'all' };
  const [searchParams, setSearchParams] = useSearchParams();

  const lockedStatus = useMemo(() => {
    if (params.listType === 'all') return undefined;
    return LOCKED_STATUSES.includes(params.listType as UserStatus) ? params.listType : undefined;
  }, [params.listType]);

  const registrationParam = searchParams.get('registrationStatus');
  const registrationStatusFilter = useMemo(() => {
    if (lockedStatus) return undefined;
    return REGISTRATION_FILTERS.includes(registrationParam as RegistrationFilter)
      ? (registrationParam as RegistrationFilter)
      : undefined;
  }, [lockedStatus, registrationParam]);

  useLoadAllAttendeesList(lockedStatus, registrationStatusFilter);
  const dispatch = useAppDispatch();

  useEffect(() => {
    return () => {
      dispatch(resetAllAttendees());
    };
  }, [dispatch]);

  const users = useAppSelector(selectAllAttendeesUsers);
  const loading = useAppSelector(selectAllAttendeesLoading);
  const total = useAppSelector(selectAllAttendeesTotal);
  const page = useAppSelector(selectAllAttendeesPage);
  const pageSize = useAppSelector(selectAllAttendeesPageSize);
  const currentSort = useAppSelector(selectAllAttendeesSort);
  const currentSortDirection = useAppSelector(selectAllAttendeesSortDirection);
  const registrationFields = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const channel = useAppSelector(selectActiveChannel);
  const userChannel = useAppSelector(selectUserChannel);
  const projectUuid = useAppSelector(selectWorkingProjectUuid);

  const defaultColumnIds = useMemo(
    () => (isWebinarChannel(userChannel?.type) ? DEFAULT_COLUMN_IDS_WEBINAR : DEFAULT_COLUMN_IDS_TOWNHALL),
    [userChannel?.type]
  );
  const totalPages = Math.ceil(total / Number(pageSize));

  const handleFieldsChange = useCallback(
    (cols: string[]) => dispatch(setAllAttendeesFields(buildAttendeeFields(cols))),
    [dispatch]
  );

  const { enabledColumns, columnOrder, handleColumnToggle, handleColumnReorder } = useColumnState({
    storageKey: projectUuid ? `cols-${projectUuid}` : null,
    searchParams,
    setSearchParams,
    defaultColumnIds,
    onFieldsChange: handleFieldsChange,
  });

  const {
    selectedProfiles,
    addToListPopover,
    displayedUsers,
    selectedEmails,
    allDisplayedSelected,
    someDisplayedSelected,
    handleSelectAll,
    handleSelectOne,
    handleAddSingleUserToList,
    handleSaveAddToList,
    clearSelection,
    getHeight,
  } = useProfileSelection({ users: users as AttendeeUserDefault[], channel: channel ?? undefined, projectUuid });

  const visibleColumns = useVisibleColumns(columnOrder, enabledColumns, registrationFields);

  const handleSort = useCallback(
    (sortKey: string) => () => {
      const nextDirection = currentSort === sortKey && currentSortDirection === 'asc' ? 'desc' : 'asc';
      dispatch(setAllAttendeesSort({ sort: sortKey, sortDirection: nextDirection }));
    },
    [currentSort, currentSortDirection, dispatch]
  );

  return (
    <Container
      data-component="AllProfiles"
      className="flex fd-column overflow-hidden"
      height="calc(100vh - 136px)"
      minHeight="calc(100vh - 136px)"
    >
      <ProfileListActions lockedStatus={lockedStatus} />

      <AllProfilesBulkActions
        selectedCount={selectedProfiles.length || 0}
        onClearSelection={clearSelection}
        onAddToList={addToListPopover.setOpenPopover}
      />
      <AddUserToList onSave={handleSaveAddToList} />
      <Container minHeight={getHeight()} height={getHeight()}>
        <OptionalComponent display={(!users || !users.length) && !loading}>
          <NoProfiles isAllProfiles={params.listType === ALL_PROFILES} />
        </OptionalComponent>
        <OptionalComponent display={!!users && users.length > 0}>
          <Container
            opacity={loading ? 0.6 : 1}
            inert={loading}
            style={{ overflow: 'auto' }}
            minHeight={getHeight()}
            height={getHeight()}
          >
            <Table loading={loading} inert={loading}>
              <ProfileTableHead
                visibleColumns={visibleColumns}
                allDisplayedSelected={allDisplayedSelected}
                someDisplayedSelected={someDisplayedSelected}
                onSelectAll={handleSelectAll}
                currentSort={currentSort}
                currentSortDirection={currentSortDirection}
                onSort={handleSort}
                loading={loading}
              />
              <TableBody>
                {displayedUsers.map((user) => (
                  <ProfileListRow
                    key={`${user.bl_profile}-${user.email}`}
                    user={user}
                    visibleColumns={visibleColumns}
                    selected={selectedEmails.has(user.email)}
                    onSelect={handleSelectOne}
                    onAddClick={handleAddSingleUserToList}
                  />
                ))}
              </TableBody>
            </Table>
          </Container>
        </OptionalComponent>
      </Container>

      <ProfileTableFooter
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => dispatch(setAllAttendeesPage(p))}
        onPageSizeChange={(size) => dispatch(setAllAttendeesPageSize(size))}
        selectId="profile-list-page-count"
      />

      <ProfileColumnsSelect
        enabledColumns={enabledColumns}
        columnOrder={columnOrder}
        onToggle={handleColumnToggle}
        onReorder={handleColumnReorder}
      />
    </Container>
  );
};

export default AllProfiles;
